<template>
  <div class="DatePickerDays" :class="{ horizontal }">
    <div class="calendar">
      <div class="monthSelector">
        <Button text size="small" severity="secondary" @click="previousMonth()">
          <i class="pi pi-chevron-left"></i>
        </Button>
        <div class="month" @click="emit('showMonths')">
          {{ monthLabel }}
        </div>
        <div class="year" @click="emit('showYears')">
          {{ yearShown }}
        </div>
        <Button text size="small" severity="secondary" @click="nextMonth()">
          <i class="pi pi-chevron-right"></i>
        </Button>
      </div>
      <div class="dayNames">
        <div v-for="dayName in dayNames" class="dayName">
          {{ dayName }}
        </div>
      </div>
      <hr>
      <div class="monthCalendar">
        <div v-for="day in days" class="day" :class="dayClass(day)" @click="setDay(day)">
          {{ day.dayNumber }}
        </div>
      </div>
    </div>
    <template v-if="date">
      <DatePickerTime v-model:date="date"></DatePickerTime>
    </template>
  </div>
</template>

<script setup>
import { computed, ref } from "vue";
import { watchImmediate } from "@vueuse/core";
import DatePickerTime from "./DatePickerTime.vue";
import { zoneDate } from "./timezones.js";

let props = defineProps([
  "date",
  "horizontal"
]);

let emit = defineEmits([
  "showMonths",
  "showYears"
]);

let timezone = computed(() => store.sessions[0].system.timezone.value);

let date = defineModel("date");
let dateOrNow = computed(() => {
  return props.date ?? zoneDate(new Date(), timezone.value);
});

let yearShown = ref(null);
let monthShown = ref(null);
watchImmediate(date, () => {
  yearShown.value = dateOrNow.value.getUTCFullYear();
  monthShown.value = dateOrNow.value.getUTCMonth();
});

function previousMonth() {
  monthShown.value -= 1;
  if (monthShown.value < 0) {
    monthShown.value = 11;
    yearShown.value -= 1;
  }
}

function nextMonth() {
  monthShown.value += 1;
  if (monthShown.value > 11) {
    monthShown.value = 0;
    yearShown.value += 1;
  }
}

const dayNames = [ "Su", "Mo", "Tu", "We", "Th", "Fr", "Sa" ];
const monthNames = [ "January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December" ];

let monthLabel = computed(() => {
  return monthNames[monthShown.value];
});

let days = computed(() => {
  let days = [];
  let firstDayOfMonth = Date.UTC(yearShown.value, monthShown.value, 1);
  let firstDayOnCalendar = startOfWeekUtc(firstDayOfMonth);
  const daysOnCalendar = 42;
  for (let i = 0; i < daysOnCalendar; i++) {
    let value = addDays(firstDayOnCalendar, i);
    days.push({
      value,
      yearNumber: value.getUTCFullYear(),
      monthNumber: value.getUTCMonth(),
      dayNumber: value.getUTCDate()
    });
  }
  return days;
});

function setDay(day) {
  date.value = new Date(Date.UTC(
    day.yearNumber,
    day.monthNumber,
    day.dayNumber
  ));
}

function startOfWeekUtc(date) {
  let newDate = startOfDayUtc(date);
  let dayOfWeek = newDate.getUTCDay();
  let dayOfMonth = newDate.getUTCDate();
  newDate.setUTCDate(dayOfMonth - dayOfWeek);
  return newDate;
}

function startOfDayUtc(date) {
  let newDate = new Date(date);
  newDate.setUTCHours(0);
  newDate.setUTCMinutes(0);
  newDate.setUTCSeconds(0);
  newDate.setUTCMilliseconds(0);
  return newDate;
}

function addDays(date, days) {
  return new Date(date.getTime() + (days * 24 * 3600000));
}

function isSameMonthUtc(date1, date2) {
  return (
    date1.getUTCFullYear() == date2.getUTCFullYear() &&
    date1.getUTCMonth() == date2.getUTCMonth()
  );
}

function isSameDayUtc(date1, date2) {
  return (
    isSameMonthUtc(date1, date2) &&
    date1.getUTCDate() == date2.getUTCDate()
  );
}

function dayClass(day) {
  return {
    today: isSameDayUtc(day.value, zoneDate(new Date(), timezone.value)),
    selectedDate: props.date && isSameDayUtc(day.value, props.date),
    outOfMonth: day.value.getUTCMonth() != monthShown.value
  };
}
</script>

<style scoped>
.p-button {
  border: 0;
}
.DatePickerDays {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.DatePickerDays.horizontal {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 50px;
}
.calendar {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.monthSelector {
  display: flex;
  align-items: center;
  gap: 10px;
}
.month, .year {
  flex-grow: 1;
  flex-basis: 0;
  padding: 10px;
  text-align: center;
  font-size: large;
  border-radius: 10px;
}
.month:hover, .year:hover {
  background: var(--gray-100);
}
.dayNames {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
}
.monthCalendar {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
}
.monthCalendar {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
}
.dayName {
  padding: 10px;
  font-size: 18px;
  font-weight: bold;
  text-align: center;
}
.day {
  height: 45px;
  padding: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  font-size: 18px;
  border-radius: 5px;
}
.day:hover {
  background: var(--gray-100);
}
.day.today {
  font-weight: bold;
  text-decoration: underline;
}
.day.outOfMonth {
  opacity: 0.5;
}
.day.selectedDate {
  color: white;
  background: var(--blue-400);
}


</style>